import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_DURATION = 4200;
const MAX_TOASTS = 5;

let globalId = 0;

/**
 * Toast queue — mount ToastViewport once (via ToastProvider).
 */
export function useToastState() {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    ({ variant = "success", message, duration = DEFAULT_DURATION, title }) => {
      const id = ++globalId;
      const item = { id, variant, message, title, exiting: false };

      setToasts((prev) => {
        const next = [...prev, item];
        return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next;
      });

      if (duration > 0) {
        const timer = window.setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  const itemLabel = useCallback(
    (itemKey) => t(`common.toast.items.${itemKey}`, itemKey),
    [t]
  );

  const success = useCallback(
    (message, options) => push({ variant: "success", message, ...options }),
    [push]
  );

  const error = useCallback(
    (message, options) => push({ variant: "error", message, ...options }),
    [push]
  );

  const info = useCallback(
    (message, options) => push({ variant: "info", message, ...options }),
    [push]
  );

  const created = useCallback(
    (itemKey, options) =>
      success(t("common.toast.created", { item: itemLabel(itemKey) }), options),
    [success, t, itemLabel]
  );

  const updated = useCallback(
    (itemKey, options) =>
      success(t("common.toast.updated", { item: itemLabel(itemKey) }), options),
    [success, t, itemLabel]
  );

  const deleted = useCallback(
    (itemKey, options) =>
      success(t("common.toast.deleted", { item: itemLabel(itemKey) }), options),
    [success, t, itemLabel]
  );

  const saved = useCallback(
    (options) => success(t("common.toast.saved"), options),
    [success, t]
  );

  const createFailed = useCallback(
    (itemKey, options) =>
      error(t("common.toast.createFailed", { item: itemLabel(itemKey) }), options),
    [error, t, itemLabel]
  );

  const updateFailed = useCallback(
    (itemKey, options) =>
      error(t("common.toast.updateFailed", { item: itemLabel(itemKey) }), options),
    [error, t, itemLabel]
  );

  const deleteFailed = useCallback(
    (itemKey, options) =>
      error(t("common.toast.deleteFailed", { item: itemLabel(itemKey) }), options),
    [error, t, itemLabel]
  );

  const genericError = useCallback(
    (options) => error(t("common.toast.error"), options),
    [error, t]
  );

  const suspended = useCallback(
    (options) => success(t("common.toast.suspended"), options),
    [success, t]
  );

  const suspendFailed = useCallback(
    (options) => error(t("common.toast.suspendFailed"), options),
    [error, t]
  );

  return {
    toasts,
    dismiss,
    success,
    error,
    info,
    created,
    updated,
    deleted,
    saved,
    createFailed,
    updateFailed,
    deleteFailed,
    genericError,
    suspended,
    suspendFailed,
  };
}
